import React, { useEffect, useState } from 'react';
import FormSection from '../../../../components/atom/FormSection';
import { useFormik } from 'formik';
import { Button, CircularProgress, Grid, TextField } from '@material-ui/core';
import AutocompleteSelect from '../../../../components/atom/AutocompleteSelect';
import { onlyVendorAutocomplete } from '../../../../autocomplete';
import { Alert } from '@material-ui/lab';
import useExcelExport from '../../../../hooks/useExcelExport';
import { EXCEL_EXPORT_VARIANTS } from '../../../../tools/excelExportSchemas';

export const detailedSearchFields = {
    vendorId: { key: 'vendorId', label: 'Vendor' },
    batchNo: { key: 'batchNo', label: 'Batch #' },
    fromDate: { key: 'fromDate', label: 'From' },
    endDate: { key: 'endDate', label: 'End' },
    invoiceNo: { key: 'invoiceNo', label: 'Vendor invoice #' },
    paymentRef: { key: 'paymentRef', label: 'Payment reference #' }
};

const DetailedSearchBlock = ({ filters, setFilters, reload, total }) => {
    const [apiError, setApiError] = useState(null);

    const { isLoading, handleExport } = useExcelExport();

    const onExport = async data => {
        setApiError(null);
        setSubmitting(true);
        try {
            await handleExport({
                total,
                variant: EXCEL_EXPORT_VARIANTS.BATCHES,
                params: {
                    vendorId: data.vendorId,
                    batchNo: data.batchNo,
                    fromDate: data.fromDate,
                    endDate: data.endDate,
                    invoiceNo: data.invoiceNo,
                    paymentRef: data.paymentRef
                }
            });
        } catch (error) {
            console.log('error', error);
            setApiError('Failed to export data. Please try again.');
        }
        setSubmitting(false);
    };

    const {
        values,
        isValid,
        isSubmitting,
        setFieldValue,
        handleSubmit,
        setSubmitting,
        setValues
    } = useFormik({
        initialValues: filters,
        onSubmit: data => onExport(data)
    });

    const handleInputChange = (event, field) => {
        const { value } = event.target;
        setFieldValue(field, value);
        setApiError(null);
    };

    useEffect(() => {
        setValues(
            Object.keys(filters).reduce((acc, key) => {
                acc[key] = filters[key];
                return acc;
            }, {})
        );
    }, [filters, setValues]);

    const formDisabled = isSubmitting || isLoading;

    return (
        <FormSection title="Detailed search">
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label={detailedSearchFields.batchNo.label}
                            fullWidth
                            type="text"
                            value={values.batchNo}
                            onChange={e => handleInputChange(e, 'batchNo')}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleteSelect
                            initCall={ids => onlyVendorAutocomplete.init(ids, true)}
                            autocompleteCall={(search, pageSize) =>
                                onlyVendorAutocomplete.search(search, pageSize, true)
                            }
                            value={values.vendorId ?? null}
                            setValue={value => {
                                setFieldValue('vendorId', value ? value : null);
                            }}
                            disabled={formDisabled}
                        >
                            <TextField label={detailedSearchFields.vendorId.label} />
                        </AutocompleteSelect>
                    </Grid>
                    <Grid item container justifyContent="center">
                        <TextField
                            type="date"
                            label={detailedSearchFields.fromDate.label}
                            fullWidth
                            value={values.fromDate}
                            name="fromDate"
                            onChange={e => handleInputChange(e, 'fromDate')}
                            InputLabelProps={{
                                shrink: true,
                                required: true
                            }}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item container justifyContent="center">
                        <TextField
                            type="date"
                            label={detailedSearchFields.endDate.label}
                            fullWidth
                            value={values.endDate}
                            name="endDate"
                            onChange={e => handleInputChange(e, 'endDate')}
                            InputLabelProps={{
                                shrink: true,
                                required: true
                            }}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={detailedSearchFields.invoiceNo.label}
                            fullWidth
                            type="text"
                            value={values.invoiceNo}
                            onChange={e => handleInputChange(e, 'invoiceNo')}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={detailedSearchFields.paymentRef.label}
                            fullWidth
                            type="text"
                            value={values.paymentRef}
                            onChange={e => handleInputChange(e, 'paymentRef')}
                            disabled={formDisabled}
                        />
                    </Grid>
                    {apiError && (
                        <Grid item xs={12}>
                            <Alert severity="error">{apiError}</Alert>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <Button
                                    fullWidth
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        setFilters(values);
                                        reload();
                                    }}
                                    disabled={formDisabled}
                                >
                                    Search
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    fullWidth
                                    size="small"
                                    variant="contained"
                                    color="default"
                                    type="submit"
                                    disabled={!isValid || isSubmitting}
                                    startIcon={
                                        isSubmitting || isLoading ? (
                                            <CircularProgress size={20} />
                                        ) : null
                                    }
                                >
                                    Export
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </form>
        </FormSection>
    );
};

export default DetailedSearchBlock;
